"use client";

import { useEffect, useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { SunIcon, MoonIcon, MonitorIcon } from "lucide-react";

type ThemeMode = "light" | "dark" | "system";

const STORAGE_KEY = "theme";

function applyTheme(mode: ThemeMode) {
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const isDark = mode === "dark" || (mode === "system" && prefersDark);
  document.documentElement.classList.toggle("dark", isDark);
}

function readStoredMode(): ThemeMode {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "dark" || stored === "light" || stored === "system") return stored;
  return "light";
}

export function ThemeToggle({ className = "" }: { className?: string }) {
  const { t } = useLingui();
  const [mode, setMode] = useState<ThemeMode>("light");

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- hydration-safe localStorage read: must run client-side post-mount; deriving during render would cause an SSR/client hydration mismatch
    setMode(readStoredMode());
  }, []);

  useEffect(() => {
    if (mode === "system") {
      const mql = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => { applyTheme("system"); };
      mql.addEventListener("change", handler);
      return () => { mql.removeEventListener("change", handler); };
    }
  }, [mode]);

  const choose = (next: ThemeMode) => {
    setMode(next);
    window.localStorage.setItem(STORAGE_KEY, next);
    applyTheme(next);
  };

  const options: { value: ThemeMode; icon: typeof SunIcon; label: string }[] = [
    { value: "light", icon: SunIcon, label: t`Light` },
    { value: "dark", icon: MoonIcon, label: t`Dark` },
    { value: "system", icon: MonitorIcon, label: t`System` },
  ];

  return (
    <div
      role="radiogroup"
      aria-label={t`Theme`}
      className={`inline-flex items-center gap-0.5 rounded-md border border-border-subtle bg-surface-muted p-0.5 ${className}`}
    >
      {options.map(({ value, icon: Icon, label }) => {
        const active = mode === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={label}
            title={label}
            onClick={() => { choose(value); }}
            className={`inline-flex items-center justify-center rounded p-1.5 transition ${
              active
                ? "bg-surface-elevated text-foreground shadow-sm"
                : "text-foreground-muted hover:text-foreground"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
          </button>
        );
      })}
    </div>
  );
}
